import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAdminAuth } from "../../context/AdminAuthContext";
import {
  Plus,
  Pencil,
  Trash2,
  GripVertical,
  ArrowLeft,
  Package,
  Copy,
  Film,
} from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminProducts() {
  const { admin } = useAdminAuth();
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [products, setProducts] = useState([]);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [duplicating, setDuplicating] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [eventRes, productsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/events/${eventId}`, {
          headers: { Authorization: `Bearer ${admin.token}` },
        }),
        fetch(`${API_URL}/api/admin/events/${eventId}/products`, {
          headers: { Authorization: `Bearer ${admin.token}` },
        }),
      ]);
      const eventData = await eventRes.json();
      const productsData = await productsRes.json();
      setEvent(eventData);
      setProducts(productsData);
    } catch (err) {
      console.error("Failed to fetch:", err);
    } finally {
      setLoading(false);
    }
  }, [admin.token, eventId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (productId) => {
    if (!window.confirm("Delete this product?")) return;
    setDeleting(productId);
    try {
      await fetch(`${API_URL}/api/admin/products/${productId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${admin.token}` },
      });
      fetchData();
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setDeleting(null);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const reordered = Array.from(products);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setProducts(reordered);

    try {
      await fetch(`${API_URL}/api/admin/events/${eventId}/products/reorder`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${admin.token}`,
        },
        body: JSON.stringify({ orderedIds: reordered.map((p) => p.id) }),
      });
    } catch (err) {
      console.error("Reorder failed:", err);
      fetchData();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#333] border-t-[#D8C08F] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-products-page">
      <button
        onClick={() => navigate("/admin/events")}
        className="flex items-center gap-2 text-[#888] hover:text-white text-sm font-sans mb-6 transition-colors"
        data-testid="back-to-events"
      >
        <ArrowLeft size={16} />
        Back to Events
      </button>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-white text-2xl font-sans font-semibold">
            Products
          </h1>
          <p className="text-[#666] text-sm font-sans mt-1">
            {event?.brand} — {products.length} product{products.length !== 1 ? "s" : ""}
          </p>
        </div>
        <button
          onClick={() => navigate(`/admin/events/${eventId}/products/new`)}
          className="flex items-center gap-2 bg-[#D8C08F] text-black font-semibold px-4 py-2.5 rounded-lg text-sm font-sans hover:bg-[#c5ad7d] transition-colors"
          data-testid="create-product-button"
        >
          <Plus size={16} />
          Add Product
        </button>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="products-list">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="space-y-3"
              data-testid="products-list"
            >
              {products.map((product, index) => (
                <Draggable
                  key={product.id}
                  draggableId={product.id}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={`bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-4 flex items-center gap-4 transition-shadow ${
                        snapshot.isDragging ? "shadow-xl shadow-black/50 border-[#D8C08F]/30" : ""
                      }`}
                      data-testid={`product-row-${product.id}`}
                    >
                      {/* Drag handle */}
                      <div
                        {...provided.dragHandleProps}
                        className="cursor-grab active:cursor-grabbing text-[#555] hover:text-[#888]"
                        data-testid={`drag-handle-${product.id}`}
                      >
                        <GripVertical size={18} />
                      </div>

                      {/* Thumbnail */}
                      <div className="w-14 h-14 rounded-lg overflow-hidden bg-[#111] shrink-0 relative">
                        {(() => {
                          const thumb = product.image || (product.mediaType === "video" ? product.posterUrl : null);
                          if (thumb) {
                            return (
                              <img
                                src={thumb}
                                alt={product.name}
                                className="w-full h-full object-cover"
                              />
                            );
                          }
                          if (product.mediaType === "video") {
                            return (
                              <div className="w-full h-full flex items-center justify-center bg-[#1c1c2a]">
                                <Film size={18} className="text-[#D8C08F]/60" />
                              </div>
                            );
                          }
                          return (
                            <div className="w-full h-full flex items-center justify-center text-[#333]">
                              <Package size={18} />
                            </div>
                          );
                        })()}
                        {product.mediaType === "video" && (
                          <div className="absolute bottom-0.5 right-0.5 bg-black/70 rounded px-1 py-px" data-testid={`video-badge-${product.id}`}>
                            <Film size={10} className="text-[#D8C08F]" />
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white text-sm font-sans font-medium truncate">
                          {product.name}
                        </h3>
                        <p className="text-[#888] text-xs font-sans">
                          {product.brand}
                        </p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-[#555] text-xs font-sans line-through">
                            ${product.retailPrice?.toFixed(2)}
                          </span>
                          <span className="text-[#D8C08F] text-xs font-sans font-medium">
                            ${product.salePrice?.toFixed(2)}
                          </span>
                          <span className="text-[#555] text-xs font-sans">
                            Sizes: {product.sizes?.join(", ") || "—"}
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={async () => {
                            setDuplicating(product.id);
                            try {
                              await fetch(`${API_URL}/api/admin/products/${product.id}/duplicate`, {
                                method: "POST",
                                headers: { Authorization: `Bearer ${admin.token}` },
                              });
                              fetchData();
                            } catch (err) {
                              console.error("Duplicate failed:", err);
                            } finally {
                              setDuplicating(null);
                            }
                          }}
                          disabled={duplicating === product.id}
                          className="p-2 rounded-lg hover:bg-[#D8C08F]/10 text-[#555] hover:text-[#D8C08F] transition-colors disabled:opacity-50"
                          title="Duplicate"
                          data-testid={`duplicate-product-${product.id}`}
                        >
                          <Copy size={16} />
                        </button>
                        <button
                          onClick={() =>
                            navigate(
                              `/admin/events/${eventId}/products/${product.id}/edit`
                            )
                          }
                          className="p-2 rounded-lg hover:bg-white/5 text-[#555] hover:text-white transition-colors"
                          title="Edit"
                          data-testid={`edit-product-${product.id}`}
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(product.id)}
                          disabled={deleting === product.id}
                          className="p-2 rounded-lg hover:bg-red-500/10 text-[#555] hover:text-red-400 transition-colors disabled:opacity-50"
                          title="Delete"
                          data-testid={`delete-product-${product.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {products.length === 0 && (
        <div className="text-center py-16">
          <Package size={40} className="text-[#333] mx-auto mb-4" />
          <p className="text-[#666] text-sm font-sans">
            No products in this event yet.
          </p>
        </div>
      )}
    </div>
  );
}
